import { transformOrderItemsToCart } from "@/helpers/tableOrderItemHelper";
import {
  fetchTableInfo,
  fetchTableOrder,
  loadCartItemsFromStorage
} from "@/services/tableOrderService";
import liveObjectsService from "@/helpers/liveObjectsService";
import {
  getAblyApiKey,
  extractSubdomainFromBaseUrl
} from "@/helpers/realtimeConfig";
import { calculateTableOrderTax } from "@/helpers/tableOrderTaxCalculator";
import {
  findMatchingCartItem,
  prepareCartItem,
  compareNewOrderWithCart,
  calculateItemTotals,
  compareModifiers,
  normalizeNote,
  getOrderItemProductId
} from "@/helpers/tableOrderCartHelper";
import {
  persistCartItemsToDb,
  deleteItemFromDb,
  clearAllItemsFromDb
} from "@/helpers/tableOrderPersistenceHelper";
import { getNewItems } from "@/helpers/tableOrderItemFilters";
import { ORDER_STATUS, BREAKPOINTS } from "@/helpers/tableOrderConstants";
import indexDbHelper from "@/helpers/indexDbHelper";
import { clearAllOrderData } from "@/helpers/orderCleanupHelper";

export const tableOrder = {
  namespaced: true,
  state: {
    tableId: null,
    tableName: null,
    tableHash: null,
    currentOrderId: null,
    existingOrder: null,
    items: [],
    cartItems: [],
    subtotal: 0,
    tipAmount: 0,
    totalTax: 0,
    serve_at: "",
    ablyUnsubscribe: null
  },
  getters: {
    getTableId: (state) => state.tableId,
    getTableName: (state) => state.tableName,
    getTableHash: (state) => state.tableHash,
    getCurrentOrderId: (state) => state.currentOrderId,
    getExistingOrder: (state) => state.existingOrder,
    hasTableInfo: (state) => !!state.tableId && !!state.tableName,
    hasActiveOrder: (state) =>
      !!state.currentOrderId &&
      state.existingOrder?.status !== ORDER_STATUS.CLOSED &&
      state.existingOrder?.status !== ORDER_STATUS.CANCELLED,
    getCartItems: (state) => state.cartItems,
    totalItems: (state) => {
      return state.cartItems.reduce((sum, item) => sum + item.quantity, 0);
    },
    getNewItems: (state) => {
      return getNewItems(state.cartItems);
    },
    getSentItems: (state) => {
      return state.cartItems.filter((item) => item.isSent === true);
    },
    getSubtotal: (state) => state.subtotal,
    getTipAmount: (state) => state.tipAmount,
    getTotalTax: (state) => state.totalTax,
    subtotal: (state) => state.subtotal,
    newItemsSubtotal: (state) => {
      const newItems = getNewItems(state.cartItems);
      return newItems.reduce((sum, item) => {
        const modifiersPrice =
          item.selectedModifiers?.reduce((mSum, m) => mSum + m.price, 0) || 0;
        return sum + (item.price + modifiersPrice) * item.quantity;
      }, 0);
    },
    newItemsTax: (state) => {
      const newItems = getNewItems(state.cartItems);
      return newItems.reduce((sum, item) => {
        const itemTax = item.tax || 0;
        return sum + itemTax * item.quantity;
      }, 0);
    }
  },
  mutations: {
    SET_TABLE_INFO(state, { tableId, tableName, tableHash }) {
      state.tableId = tableId;
      state.tableName = tableName;
      state.tableHash = tableHash;
    },
    SET_CURRENT_ORDER_ID(state, orderId) {
      state.currentOrderId = orderId;
    },
    SET_EXISTING_ORDER(state, order) {
      state.existingOrder = order;
    },
    CLEAR_TABLE_INFO(state) {
      state.tableId = null;
      state.tableName = null;
      state.tableHash = null;
    },
    CLEAR_ORDER_INFO(state) {
      state.currentOrderId = null;
      state.existingOrder = null;
    },
    SET_ABLY_UNSUBSCRIBE(state, unsubscribeFn) {
      state.ablyUnsubscribe = unsubscribeFn;
    },
    CLEAR_ALL(state) {
      state.tableId = null;
      state.tableName = null;
      state.tableHash = null;
      state.currentOrderId = null;
      state.existingOrder = null;
    },

    ADD_ITEM(state, payload) {
      state.items.push(payload);
    },
    UPDATE_ITEM(state, { index, item }) {
      state.items.splice(index, 1, item);
    },
    REMOVE_ITEM(state, index) {
      state.items.splice(index, 1);
    },

    SET_CART_ITEMS(state, items) {
      state.cartItems = items;
    },
    CLEAR_MODIFIED_FLAGS(state) {
      state.cartItems.forEach((item) => {
        if (item.wasModifiedByStaff === true) {
          delete item.wasModifiedByStaff;
          delete item.isModified;
          delete item.originalQuantity;
          delete item.newQuantity;
          delete item.isNew;
        }
      });
    },
    ADD_TO_CART(state, cartItem) {
      const itemToAdd = {
        ...cartItem,
        local_id:
          cartItem.local_id || cartItem.cartId || `${cartItem.id}-${Date.now()}`
      };
      state.cartItems.push(itemToAdd);
    },
    UPDATE_QUANTITY(state, { cartId, delta }) {
      const item = state.cartItems.find((item) => item.cartId === cartId);
      if (item) {
        if (item.isSent === true) {
          console.warn(
            "[tableOrder] Cannot update quantity for sent item:",
            cartId
          );
          return;
        }
        item.quantity = Math.max(1, item.quantity + delta);

        const {
          itemTotal,
          itemTotalWithoutDiscount,
          hasDiscount,
          modifiersText
        } = calculateItemTotals({
          price: item.price,
          modifiers: item.selectedModifiers || [],
          quantity: item.quantity,
          discount: item.discount || 0,
          wholeDiscount: item.wholeDiscount || 0
        });

        item.itemTotal = itemTotal;
        item.itemTotalWithoutDiscount = itemTotalWithoutDiscount;
        item.hasDiscount = hasDiscount;
        item.modifiersText = modifiersText;
        item.isNew = true;
        item.isModified = false;
        item.originalQuantity = null;

        if (
          state.existingOrder &&
          state.existingOrder.items &&
          state.existingOrder.items.length > 0
        ) {
          const matchingOrderItem = state.existingOrder.items.find(
            (orderItem) => {
              const orderProductId = getOrderItemProductId(orderItem);
              const cartProductId = item.id ?? item.product?.id ?? null;

              if (
                orderProductId == null ||
                cartProductId == null ||
                String(orderProductId) !== String(cartProductId)
              ) {
                return false;
              }

              const orderModifiers = orderItem.modifiers || [];

              const cartModifiers =
                item.selectedModifiers || item.modifiers || [];
              if (!compareModifiers(orderModifiers, cartModifiers)) {
                return false;
              }

              if (
                normalizeNote(orderItem.note || orderItem.notes || "") !==
                normalizeNote(item.note || "")
              ) {
                return false;
              }

              return true;
            }
          );

          if (matchingOrderItem) {
            const orderQuantity = matchingOrderItem.quantity || 1;
            item.isNew = false;
            item.isModified = Boolean(item.quantity !== orderQuantity);
            item.originalQuantity = Boolean(item.quantity !== orderQuantity)
              ? orderQuantity
              : null;
          }
        }

        /* ******************** Save to IndexDB ******************** */
        const newItems = state.cartItems.filter((item) => item.isSent !== true);
        const itemsForDb = newItems
          .map((item) => ({
            ...item,
            local_id: item.local_id || item.cartId
          }))
          .filter((item) => item.local_id);

        if (itemsForDb.length > 0) {
          indexDbHelper.saveToDb("tableOrderItems", itemsForDb).catch((err) => {
            console.error("Error saving cartItems to IndexDB:", err);
          });
        }
      }
    },
    REMOVE_ITEM_CART(state, cartId) {
      const itemToRemove = state.cartItems.find(
        (item) => item.cartId === cartId
      );
      if (itemToRemove) {
        if (itemToRemove.isSent === true) {
          console.warn("[tableOrder] Cannot remove sent item:", cartId);
          return;
        }
      }
      state.cartItems = state.cartItems.filter(
        (item) => item.cartId !== cartId
      );
    },
    REPLACE_CART_ITEM(state, { cartId, cartItem }) {
      const index = state.cartItems.findIndex((item) => item.cartId === cartId);
      if (index === -1) return;
      const existing = state.cartItems[index];
      if (existing.isSent === true) return;
      const itemToSave = {
        ...cartItem,
        cartId: existing.cartId,
        local_id: existing.local_id || existing.cartId
      };
      state.cartItems.splice(index, 1, itemToSave);
    },
    RECALCULATE(state, { rootGetters }) {
      const store = rootGetters.getStore || {};
      const storeConfig = {
        taxRound: rootGetters.getTaxRound || "",
        isStoreTaxInclusive: rootGetters.isStoreTaxInclusive || false,
        taxRates: store.tax_rates || []
      };

      const activeItems =
        state.cartItems.length > 0 ? state.cartItems : state.items;

      const { totalTax, itemsWithTax, subtotal } = calculateTableOrderTax(
        activeItems,
        storeConfig
      );

      state.subtotal = subtotal;
      state.totalTax = totalTax;

      activeItems.forEach((item, index) => {
        const itemWithTax = itemsWithTax[index];
        if (itemWithTax) {
          Object.assign(item, {
            tax: itemWithTax.tax,
            tax_items: itemWithTax.tax_items,
            product: itemWithTax.product,
            tax_rate_value: itemWithTax.tax_rate_value
          });
        }
      });
    }
  },
  actions: {
    setTableInfo({ commit }, { tableId, tableName, tableHash }) {
      commit("SET_TABLE_INFO", { tableId, tableName, tableHash });
    },
    setCurrentOrderId({ commit }, orderId) {
      commit("SET_CURRENT_ORDER_ID", orderId);
    },
    setExistingOrder({ commit, state, rootGetters, dispatch }, params) {
      if (!params) {
        throw new Error("[tableOrder] setExistingOrder: params is required");
      }

      let order, newItemsFromDb, comparison, isUserSubmission;
      if (params && typeof params === "object" && params.order) {
        order = params.order;
        newItemsFromDb =
          params.newItemsFromDb !== undefined ? params.newItemsFromDb : null;
        comparison = params.comparison || null;
        isUserSubmission = params.isUserSubmission || false;
      } else {
        order = params;
        newItemsFromDb = null;
        comparison = null;
        isUserSubmission = false;
      }

      if (order && order.dining_option_type) {
        dispatch("setDiningOption", order.dining_option_type, { root: true });
      }

      const previousOrderItems = state.existingOrder?.items || [];
      commit("SET_EXISTING_ORDER", order);

      if (
        order &&
        order.id &&
        order.status !== ORDER_STATUS.CLOSED &&
        order.status !== ORDER_STATUS.CANCELLED
      ) {
        commit("SET_CURRENT_ORDER_ID", order.id);

        if (
          order.items &&
          Array.isArray(order.items) &&
          order.items.length > 0
        ) {
          const store = rootGetters.getStore || {};
          const taxRates = store.tax_rates || [];
          const isStoreTaxInclusive =
            rootGetters.isStoreTaxInclusive ||
            rootGetters["isStoreTaxInclusive"] ||
            false;

          const sentItems = transformOrderItemsToCart(order.items, {
            taxRates,
            isStoreTaxInclusive,
            filterFn: (item) => item.void_status !== 1
          });

          if (!comparison) {
            const itemsFromDb =
              newItemsFromDb !== null
                ? newItemsFromDb || []
                : getNewItems(state.cartItems);

            const {
              compareModifiers,
              normalizeNote
            } = require("@/helpers/tableOrderCartHelper");

            const actualNewItems = [];

            itemsFromDb.forEach((dbItem) => {
              const matchingOrderItem = order.items.find((orderItem) => {
                const orderProductId = getOrderItemProductId(orderItem);
                const cartProductId = dbItem.id ?? dbItem.product?.id ?? null;

                if (
                  orderProductId == null ||
                  cartProductId == null ||
                  String(orderProductId) !== String(cartProductId)
                ) {
                  return false;
                }

                const orderModifiers =
                  orderItem.modifiers || orderItem.modifier_items || [];
                const cartModifiers =
                  dbItem.selectedModifiers || dbItem.modifiers || [];
                if (!compareModifiers(orderModifiers, cartModifiers)) {
                  return false;
                }

                const orderNote = normalizeNote(
                  orderItem.note || orderItem.notes || ""
                );
                const cartNote = normalizeNote(dbItem.note || "");
                if (orderNote !== cartNote) {
                  return false;
                }

                return true;
              });

              if (!matchingOrderItem) {
                actualNewItems.push({
                  ...dbItem,
                  isNew: true,
                  isModified: false
                });
              } else {
                const orderQuantity = matchingOrderItem.quantity || 1;
                const cartQuantity = dbItem.quantity || 1;
                if (cartQuantity !== orderQuantity) {
                  actualNewItems.push({
                    ...dbItem,
                    isNew: false,
                    isModified: true,
                    originalQuantity: orderQuantity
                  });
                }
              }
            });

            const allCartItems = [...actualNewItems, ...sentItems];
            commit("SET_CART_ITEMS", allCartItems);

            if (previousOrderItems.length > 0 && !isUserSubmission) {
              const previousSentItems = transformOrderItemsToCart(
                previousOrderItems,
                {
                  taxRates,
                  filterFn: (item) => item.void_status !== 1
                }
              );

              const combinedCartItems = [
                ...previousSentItems,
                ...actualNewItems
              ];
              const newOrderItems = order.items || [];
              comparison = compareNewOrderWithCart(
                newOrderItems,
                combinedCartItems
              );
            }
          }

          if (
            comparison &&
            comparison.newItemsInOrder &&
            comparison.modifiedItems &&
            !isUserSubmission
          ) {
            const {
              compareModifiers,
              normalizeNote
            } = require("@/helpers/tableOrderCartHelper");

            sentItems.forEach((sentItem) => {
              const sentProductId = sentItem.id || sentItem.product?.id;
              const sentModifiers =
                sentItem.selectedModifiers || sentItem.modifiers || [];
              const sentNote = normalizeNote(sentItem.note || "");

              sentItem.wasModifiedByStaff = false;
              sentItem.isNew = false;
              sentItem.isModified = false;
              delete sentItem.originalQuantity;
              delete sentItem.newQuantity;

              const modifiedItem = comparison.modifiedItems.find((modItem) => {
                const modProductId = modItem.productId;

                if (String(sentProductId) !== String(modProductId)) {
                  return false;
                }

                const orderModifiers = modItem.modifiers || [];
                if (!compareModifiers(sentModifiers, orderModifiers)) {
                  return false;
                }

                const orderNote = normalizeNote(modItem.note || "");
                if (sentNote !== orderNote) {
                  return false;
                }

                return true;
              });

              if (modifiedItem) {
                sentItem.isModified = true;
                sentItem.wasModifiedByStaff = true;
                sentItem.originalQuantity = modifiedItem.oldQuantity;
                sentItem.newQuantity = modifiedItem.newQuantity;
              } else {
                const newItemInOrder = comparison.newItemsInOrder.find(
                  (newItem) => {
                    const newProductId = newItem.productId;

                    if (String(sentProductId) !== String(newProductId)) {
                      return false;
                    }

                    const orderModifiers = newItem.modifiers || [];
                    if (!compareModifiers(sentModifiers, orderModifiers)) {
                      return false;
                    }

                    const orderNote = normalizeNote(newItem.note || "");
                    if (sentNote !== orderNote) {
                      return false;
                    }

                    return true;
                  }
                );

                if (newItemInOrder) {
                  sentItem.wasModifiedByStaff = true;
                  sentItem.isNew = true;
                }
              }
            });
          } else {
            sentItems.forEach((sentItem) => {
              sentItem.wasModifiedByStaff = false;
              sentItem.isNew = false;
              sentItem.isModified = false;
              delete sentItem.originalQuantity;
              delete sentItem.newQuantity;
            });
          }

          const currentCartItems = state.cartItems || [];
          const actualNewItems = currentCartItems.filter(
            (item) => item.isSent !== true
          );

          const allCartItems = [...actualNewItems, ...sentItems];

          commit("SET_CART_ITEMS", allCartItems);
          persistCartItemsToDb(allCartItems);
          commit("RECALCULATE", { rootGetters });
        }
      } else if (
        order &&
        (order.status === ORDER_STATUS.CLOSED ||
          order.status === ORDER_STATUS.CANCELLED)
      ) {
        commit("SET_CURRENT_ORDER_ID", null);
      }
    },
    clearTableInfo({ commit }) {
      commit("CLEAR_TABLE_INFO");
    },
    clearOrderInfo({ commit }) {
      commit("CLEAR_ORDER_INFO");
    },
    clearAll({ commit }) {
      commit("CLEAR_ALL");
    },

    async initializeTableOrder(
      { dispatch, state, rootGetters },
      { storeAlias, tableHash }
    ) {
      const storeInfo = await dispatch("store/loadStoreInfo", storeAlias, {
        root: true
      });

      // Decode table hash to get table info and store_id
      const tableData = await fetchTableInfo(tableHash);

      await dispatch("setTableInfo", {
        tableId: tableData.table_id,
        tableName: tableData.table_name,
        tableHash: tableHash
      });

      // Use store_id from decoded table hash (this is actually the location_id)
      const locationId = tableData.store_id;

      if (!locationId) {
        console.error(
          "[tableOrder/initializeTableOrder] No store_id in decoded table data"
        );
        throw new Error("Store ID not found in table data");
      }

      // Load store locations to get the specific location info
      const locations = await dispatch(
        "store/loadStoreLocations",
        storeInfo.dashboardLink,
        { root: true }
      );

      // Find and set the specific location based on the locationId from the table
      if (locations && locations.length > 0) {
        const tableLocation = locations.find((loc) => loc.id === locationId);

        if (tableLocation) {
          await dispatch("app/setSelectedLocation", tableLocation, {
            root: true
          });
        } else {
          console.warn(
            "[tableOrder/initializeTableOrder] Location not found in locations list, using locationId:",
            locationId
          );
        }
      }

      // Load store configuration using the location_id from the table
      await dispatch("store/loadStoreConfig", locationId, { root: true });

      // For table orders, default to "Dine-In" if available
      const availableDiningOptions =
        rootGetters.availableDiningOptions ||
        rootGetters["store/availableDiningOptions"] ||
        [];

      const dineInOption = availableDiningOptions.find(
        (opt) => opt.name && opt.name.toLowerCase() === "dine-in"
      );

      if (dineInOption) {
        await dispatch("store/setDiningOption", dineInOption.id, {
          root: true
        });
      }

      // Initialize LiveObjects EARLY so we can check for fresh order data
      // We use tableData.store_id directly instead of waiting for store config
      const baseUrl = rootGetters.baseUrl || storeInfo.dashboardLink;
      try {
        let subdomain;
        try {
          subdomain = extractSubdomainFromBaseUrl(baseUrl);
        } catch (e) {
          console.warn("[tableOrder] Could not extract subdomain:", e);
          subdomain = storeAlias || "store";
        }

        await liveObjectsService.initialize({
          apiKey: getAblyApiKey(),
          storeId: locationId, // Use locationId for storeId in LiveObjects
          storeSubdomain: subdomain,
          clientId: `biyo-order-table-${locationId}-${Date.now()}`
        });
      } catch (e) {
        console.warn("[tableOrder] Early LiveObjects init failed:", e);
        // Continue even if LiveObjects init fails
      }

      const newItemsFromDb = await loadCartItemsFromStorage();
      const currentOrderId = state.currentOrderId;
      let existingOrder = null;
      let orderLoadedFromAbly = false;

      // 1. If we have a known Order ID, try to load it from Ably first
      if (currentOrderId) {
        try {
          const ablyOrder = await liveObjectsService.getOrderFromMap(
            currentOrderId
          );

          // Check if valid and NOT closed/cancelled
          if (
            ablyOrder &&
            ablyOrder.id &&
            ablyOrder.status !== ORDER_STATUS.CLOSED &&
            ablyOrder.status !== ORDER_STATUS.CANCELLED
          ) {
            existingOrder = ablyOrder;
            orderLoadedFromAbly = true;
            await dispatch("setExistingOrder", {
              order: existingOrder,
              newItemsFromDb: newItemsFromDb
            });
          } else {
            console.log(
              "[tableOrder] Ably order not found or closed/cancelled"
            );

            // If Ably specifically says the order is closed/cancelled, clear local state
            if (
              ablyOrder &&
              (ablyOrder.status === ORDER_STATUS.CLOSED ||
                ablyOrder.status === ORDER_STATUS.CANCELLED)
            ) {
              console.log(
                "[tableOrder] Order is closed/cancelled in Ably, clearing local state"
              );
              await dispatch("setCurrentOrderId", null);
              await clearAllOrderData({ dispatch });
            }
          }
        } catch (e) {
          console.warn("[tableOrder] Failed to load from Ably by ID:", e);
        }
      }

      // 2. If NOT loaded from Ably (or no known ID), call API to find open order
      if (!orderLoadedFromAbly) {
        try {
          console.log("[tableOrder] Fetching open order from API...");
          existingOrder = await fetchTableOrder(tableData.table_id);

          if (existingOrder) {
            // Safety check: Ensure status is not CLOSED (3) or CANCELLED (4)
            if (
              existingOrder.status === ORDER_STATUS.CLOSED ||
              existingOrder.status === ORDER_STATUS.CANCELLED
            ) {
              console.log(
                "[tableOrder] API returned closed/cancelled order, ignoring"
              );
              if (currentOrderId === existingOrder.id) {
                await dispatch("setCurrentOrderId", null);
              }
              existingOrder = null;
              await clearAllOrderData({ dispatch });
            } else {
              // Try to get fresher data from Ably (merge strategy)
              try {
                const ablyOrder = await liveObjectsService.getOrderFromMap(
                  existingOrder.id
                );
                if (
                  ablyOrder &&
                  ablyOrder.items &&
                  Array.isArray(ablyOrder.items)
                ) {
                  existingOrder = {
                    ...existingOrder,
                    ...ablyOrder
                  };
                }

                // Double check status after Ably merge
                if (
                  existingOrder.status === ORDER_STATUS.CLOSED ||
                  existingOrder.status === ORDER_STATUS.CANCELLED
                ) {
                  console.log(
                    "[tableOrder] Merged order is closed/cancelled, ignoring"
                  );
                  await dispatch("setCurrentOrderId", null);
                  existingOrder = null;
                  await clearAllOrderData({ dispatch });
                } else {
                  await dispatch("setExistingOrder", {
                    order: existingOrder,
                    newItemsFromDb: newItemsFromDb
                  });
                }
              } catch (e) {
                console.warn("[tableOrder] Failed to check Ably for order:", e);
                // Use API order even if Ably check fails
                await dispatch("setExistingOrder", {
                  order: existingOrder,
                  newItemsFromDb: newItemsFromDb
                });
              }
            }
          } else {
            // No open order returned from API
            console.log("[tableOrder] No open order found via API");
            // If we thought we had an order, clear it now
            if (currentOrderId) {
              console.log("[tableOrder] Clearing stale currentOrderId");
              await dispatch("setCurrentOrderId", null);
            }

            if (newItemsFromDb.length > 0) {
              await dispatch("setCartItems", newItemsFromDb);
            } else {
              await dispatch("setCartItems", []);
            }
          }
        } catch (orderError) {
          console.log("[tableOrder] No existing order for table:", orderError);
          // If error (e.g. 404), also ensure we clear stale state
          if (currentOrderId) {
            await dispatch("setCurrentOrderId", null);
          }

          if (newItemsFromDb.length > 0) {
            await dispatch("setCartItems", newItemsFromDb);
          } else {
            await dispatch("setCartItems", []);
          }
        }
      }

      return {
        storeInfo,
        tableData,
        existingOrder
      };
    },

    async clearCart({ commit, rootGetters }) {
      try {
        await clearAllItemsFromDb();
        commit("SET_CART_ITEMS", []);
        commit("RECALCULATE", { rootGetters });
      } catch (error) {
        console.error("[tableOrder] Failed to clear cart:", error);
        throw error;
      }
    },

    setItems({ commit, rootGetters }, payload) {
      commit("SET_ITEMS", payload);
      commit("RECALCULATE", { rootGetters });
    },
    addItem({ commit, rootGetters, state }, payload) {
      commit("ADD_ITEM", payload);
      persistCartItemsToDb(state.items);
      commit("RECALCULATE", { rootGetters });
    },
    updateItem({ commit, rootGetters, state }, { index, item }) {
      if (typeof index !== "number" || index < 0) {
        throw new Error("[tableOrder] updateItem: valid index is required");
      }
      commit("UPDATE_ITEM", { index, item });
      persistCartItemsToDb(state.items);
      commit("RECALCULATE", { rootGetters });
    },
    removeItem({ commit, rootGetters, state }, index) {
      if (typeof index !== "number" || index < 0) {
        throw new Error("[tableOrder] removeItem: valid index is required");
      }
      const itemToRemove = state.items[index];
      commit("REMOVE_ITEM", index);
      if (itemToRemove && itemToRemove.local_id) {
        deleteItemFromDb(itemToRemove.local_id);
      }
      commit("RECALCULATE", { rootGetters });
    },

    addToCart(
      { commit, state, rootGetters },
      { item, quantity, note, modifiers }
    ) {
      if (!item || !item.id) {
        throw new Error("[tableOrder] addToCart: item.id is required");
      }
      if (typeof quantity !== "number" || quantity <= 0) {
        throw new Error(
          "[tableOrder] addToCart: quantity must be a positive number"
        );
      }
      if (rootGetters["store/isProduct86d"]?.(item.id)) {
        throw new Error(`${item.name || "This item"} is unavailable.`);
      }

      const selectedModifiers = modifiers || [];
      const store = rootGetters.getStore || {};
      const taxRates = store.tax_rates || [];

      const existingItem = findMatchingCartItem(
        state.cartItems,
        item,
        selectedModifiers,
        note
      );

      commit("CLEAR_MODIFIED_FLAGS");

      if (existingItem) {
        commit("UPDATE_QUANTITY", {
          cartId: existingItem.cartId,
          delta: quantity
        });
        persistCartItemsToDb(state.cartItems);
      } else {
        const existingOrderItems = state.existingOrder?.items || [];
        const cartItem = prepareCartItem(
          item,
          quantity,
          note,
          selectedModifiers,
          taxRates,
          existingOrderItems
        );
        commit("ADD_TO_CART", cartItem);
        persistCartItemsToDb(state.cartItems);
      }

      commit("RECALCULATE", { rootGetters });

      if (
        window.innerWidth < BREAKPOINTS.MOBILE_MAX ||
        !rootGetters.isSidebarVisible
      ) {
        commit("SET_CART_OPEN", true, { root: true });
      }
    },
    updateQuantity({ commit, state, rootGetters }, { cartId, delta }) {
      if (!cartId) {
        throw new Error("[tableOrder] updateQuantity: cartId is required");
      }
      if (typeof delta !== "number") {
        throw new Error("[tableOrder] updateQuantity: delta must be a number");
      }

      const item = state.cartItems.find((item) => item.cartId === cartId);
      if (item && item.isSent === true) {
        return;
      }
      commit("UPDATE_QUANTITY", { cartId, delta });
      persistCartItemsToDb(state.cartItems);
      commit("RECALCULATE", { rootGetters });
    },
    removeItemCart({ commit, state, rootGetters }, cartId) {
      if (!cartId) {
        throw new Error("[tableOrder] removeItemCart: cartId is required");
      }

      const item = state.cartItems.find((item) => item.cartId === cartId);
      if (item && item.isSent === true) {
        return;
      }
      const idToDelete = item?.local_id || item?.cartId;
      commit("REMOVE_ITEM_CART", cartId);
      if (idToDelete) {
        deleteItemFromDb(idToDelete);
      }
      persistCartItemsToDb(state.cartItems);
      commit("RECALCULATE", { rootGetters });
      if (state.cartItems.length === 0) {
        commit("SET_CART_OPEN", false, { root: true });
      }
    },
    updateCartItem(
      { commit, state, rootGetters },
      { cartId, item, quantity, note, modifiers }
    ) {
      const existing = state.cartItems.find((i) => i.cartId === cartId);
      if (!existing || existing.isSent === true) return;
      if (rootGetters["store/isProduct86d"]?.(item.id)) {
        throw new Error(`${item.name || "This item"} is unavailable.`);
      }

      const selectedModifiers = modifiers || [];
      const store = rootGetters.getStore || {};
      const taxRates = store.tax_rates || [];
      const existingOrderItems = state.existingOrder?.items || [];
      const cartItem = prepareCartItem(
        item,
        quantity,
        note,
        selectedModifiers,
        taxRates,
        existingOrderItems
      );
      const itemToSave = {
        ...cartItem,
        cartId: existing.cartId,
        local_id: existing.local_id || existing.cartId
      };
      commit("REPLACE_CART_ITEM", { cartId, cartItem: itemToSave });
      persistCartItemsToDb(state.cartItems);
      commit("RECALCULATE", { rootGetters });
      if (
        window.innerWidth < BREAKPOINTS.MOBILE_MAX ||
        !rootGetters.isSidebarVisible
      ) {
        commit("SET_CART_OPEN", true, { root: true });
      }
    },
    setCartItems({ commit, rootGetters }, items) {
      if (!Array.isArray(items)) {
        throw new Error("[tableOrder] setCartItems: items must be an array");
      }

      commit("SET_CART_ITEMS", items);
      persistCartItemsToDb(items);
      commit("RECALCULATE", { rootGetters });
    },
    recalculate({ commit, rootGetters }) {
      commit("RECALCULATE", { rootGetters });
    },

    async initializeAbly(
      { dispatch, rootGetters },
      { onOrderUpdate = null } = {}
    ) {
      const store = rootGetters.getStore || {};
      const baseUrl = rootGetters.baseUrl;

      if (!store.id || !baseUrl) {
        console.warn(
          "[tableOrder] Cannot init LiveObjects - missing store info"
        );
        return;
      }

      try {
        const subdomain = extractSubdomainFromBaseUrl(baseUrl);

        await liveObjectsService.initialize({
          apiKey: getAblyApiKey(),
          storeId: store.id,
          storeSubdomain: subdomain,
          clientId: `biyo-order-table-${store.id}-${Date.now()}`
        });

        // Always subscribe — even with no current order. Staff can open an
        // order for this table on the POS before the customer has one; the
        // listener adopts any open online order matching our table, so the
        // drawer populates in realtime instead of waiting for a refresh.
        await dispatch("subscribeToOrderUpdates", { onOrderUpdate });
      } catch (error) {
        console.error("[tableOrder] Failed to initialize LiveObjects:", error);
        throw error;
      }
    },

    async subscribeToOrderUpdates(
      { commit, dispatch, state, getters, rootGetters },
      { onOrderUpdate = null } = {}
    ) {
      if (state.ablyUnsubscribe) {
        state.ablyUnsubscribe();
        commit("SET_ABLY_UNSUBSCRIBE", null);
      }

      const unsubscribe = await liveObjectsService.subscribeToOrderUpdates(
        async ({ data: orderData, key }) => {
          console.log("subscribeToOrderUpdates", orderData);
          // Ably echoes map ops back to the publisher — ignore our own.
          if (
            orderData?.source &&
            orderData.source === liveObjectsService.getClientId()
          ) {
            return;
          }

          const currentId = getters.getCurrentOrderId?.toString();
          const matchesCurrentOrder =
            !!currentId &&
            (key === currentId ||
              orderData?.id?.toString() === currentId ||
              orderData?.local_id?.toString() === currentId);

          // No order adopted yet (customer still browsing): adopt an open
          // online order that staff opened for THIS table on the POS.
          // setExistingOrder stamps SET_CURRENT_ORDER_ID for open orders, so
          // later updates then match via currentId.
          const matchesOurTable =
            !currentId &&
            orderData?.table != null &&
            state.tableId != null &&
            String(orderData.table) === String(state.tableId) &&
            Number(orderData.type) === 1 &&
            orderData.status !== ORDER_STATUS.CLOSED &&
            orderData.status !== ORDER_STATUS.CANCELLED;

          if (matchesCurrentOrder || matchesOurTable) {
            const newOrderItems = orderData.items || [];
            const previousOrderItems = state.existingOrder?.items || [];

            if (!previousOrderItems || previousOrderItems.length === 0) {
              await dispatch("setExistingOrder", {
                order: orderData,
                comparison: null
              });
              return;
            }

            const store = rootGetters.getStore || {};
            const taxRates = store.tax_rates || [];

            const currentCartItems = state.cartItems || [];
            const sentItemsFromCart = currentCartItems.filter(
              (item) => item.isSent === true
            );
            const newItemsFromCart = currentCartItems.filter(
              (item) => item.isSent !== true
            );

            let itemsForComparison = [];

            if (sentItemsFromCart.length > 0) {
              itemsForComparison = [...sentItemsFromCart];
            } else {
              const previousSentItems = transformOrderItemsToCart(
                previousOrderItems,
                {
                  taxRates,
                  filterFn: (item) => item.void_status !== 1
                }
              );
              itemsForComparison = previousSentItems;
            }

            const combinedCartItems = [
              ...itemsForComparison,
              ...newItemsFromCart
            ];

            const comparison = compareNewOrderWithCart(
              newOrderItems,
              combinedCartItems
            );

            await dispatch("setExistingOrder", {
              order: orderData,
              comparison: comparison
            });

            if (
              orderData.status === ORDER_STATUS.CLOSED ||
              orderData.status === ORDER_STATUS.CANCELLED
            ) {
              // Clear all local data (IndexedDB, Vuex, localStorage)
              await clearAllOrderData({ dispatch });

              const unsubscribeFn = state.ablyUnsubscribe;
              if (unsubscribeFn) {
                unsubscribeFn();
                commit("SET_ABLY_UNSUBSCRIBE", null);
              }

              if (onOrderUpdate) {
                const isCancelled = orderData.status === ORDER_STATUS.CANCELLED;
                console.log(
                  `[tableOrder] Order ${
                    isCancelled ? "cancelled" : "closed"
                  }, calling onOrderUpdate`
                );
                onOrderUpdate({
                  type: isCancelled ? "order_cancelled" : "order_closed",
                  message: isCancelled
                    ? "Your order has been cancelled by staff."
                    : "Your order has been closed. You can place a new order.",
                  variant: isCancelled ? "warning" : "success"
                });
              }
            } else {
              const hasChanges =
                comparison &&
                ((comparison.newItemsInOrder &&
                  comparison.newItemsInOrder.length > 0) ||
                  (comparison.modifiedItems &&
                    comparison.modifiedItems.length > 0) ||
                  (comparison.removedItems &&
                    comparison.removedItems.length > 0));

              if (onOrderUpdate && hasChanges) {
                onOrderUpdate({
                  type: "order_updated",
                  message: "Your order has been updated by staff.",
                  variant: "info",
                  comparison: comparison
                });
              }
            }
          }
        }
      );

      commit("SET_ABLY_UNSUBSCRIBE", unsubscribe);
    },

    cleanupAbly({ commit, state }) {
      if (state.ablyUnsubscribe) {
        state.ablyUnsubscribe();
        commit("SET_ABLY_UNSUBSCRIBE", null);
      }
      liveObjectsService.cleanup();
    }
  }
};
